import { SiteNav } from '@vwfs-bronson/bronson-react';
import React from 'react';

import { useNavigation } from './useNavigation';

export type ItemClickCallback = (url?: string) => void;

export type DropDownItem = {
    /**
     * The prop href of the DropdownItem is now mandatory
     */
    href: string;

    /**
     * Optional URL, only considered if no click handler provided
     */
    url?: string;

    /**
     * Label of the dropdown item (e.g. FAQs)
     */
    label: string;
    /**
     * Is the dropdown item currently active (e.g. because it reflects the current page)
     */
    isActive?: boolean;
    /**
     * An optional click handler that will be used instead of the URL redirection
     * @param url The URL of the nav item if provided
     */
    onClick?: ItemClickCallback;
};

export type BaseSimpleNavItemProps = {
    /**
     * Optional URL, only considered if no click handler provided
     */
    url?: string;
    /**
     * Label of the nav item (e.g. FAQs)
     */
    label: string;
    /**
     * Is the nav item currently active (e.g. because it reflects the current page)
     */
    isActive?: boolean;
    /**
     * Should it be in the reversed area or not?
     */
    isReversed?: boolean;
    /**
     * An optional click handler that will be used instead of the URL redirection
     * @param url The URL of the nav item if provided
     */
    onClick?: ItemClickCallback;
    /**
     * Title to display inside the SimpleNavItem dropdown element
     */
    dropdownTitle?: string;
    /**
     * A collection of {@link DropDownItem} elements to render a dropdown
     */
    dropdownItems?: DropDownItem[];
};

export type BaseSimpleNavItemPropsWithoutChildren = {
    /** Must not be set when {@link children} are omitted. */
    href?: never;

    /**
     * A collection of {@link DropDownItem} elements to render a dropdown
     */
    dropdownItems: DropDownItem[];
};

export type BaseSimpleNavItemPropsWithChildren = {
    /** The `[href]` value for the {@link SiteNavItem}. */
    href: string;

    dropdownItems?: never;
};

export type SimpleNavItemPropsLinksWithout = BaseSimpleNavItemProps & BaseSimpleNavItemPropsWithoutChildren;
export type SimpleNavItemPropsLinksWith = BaseSimpleNavItemProps & BaseSimpleNavItemPropsWithChildren;

export type SimpleNavItemProps = SimpleNavItemPropsLinksWithout | SimpleNavItemPropsLinksWith;

/**
 * A simple, direct nav item without a dropdown. Could be either a link or a "fake" link with a click handler
 */
export const SimpleNavItem: React.FC<SimpleNavItemProps> = ({
    url,
    href,
    label,
    isActive,
    isReversed,
    onClick: externalClickHandler,
    dropdownTitle,
    dropdownItems,
}) => {
    const navigateTo = useNavigation();

    const onClick = (itemUrl?: string, onClick?: ItemClickCallback) => {
        if (onClick) {
            onClick(itemUrl);
        } else {
            navigateTo(itemUrl);
        }
    };

    return dropdownItems ? (
        <SiteNav.Item
            label={label}
            title={label}
            active={isActive}
            reversed={isReversed}
            onClick={() => onClick(url, externalClickHandler)}
            icon={!!dropdownItems?.length}
            dropdownTitle={dropdownTitle}
            aria-label={label}
            onKeyDown={e => {
                if (e.key === 'Enter' && onClick) {
                    onClick(url, externalClickHandler);
                }
            }}
        >
            {dropdownItems?.map(item => (
                <SiteNav.Item.DropdownItem
                    key={item.label}
                    href={item.url ? item.url : '#'}
                    onClick={() => onClick(item.url, item.onClick)}
                    active={item.isActive}
                    aria-label={item.label}
                    onKeyDown={e => {
                        if (e.key === 'Enter' && onClick) {
                            onClick(item.url, item.onClick);
                        }
                    }}
                >
                    {item.label}
                </SiteNav.Item.DropdownItem>
            ))}
        </SiteNav.Item>
    ) : (
        <SiteNav.Item
            label={label}
            title={label}
            href={href}
            active={isActive}
            reversed={isReversed}
            onClick={() => onClick(url, externalClickHandler)}
            dropdownTitle={dropdownTitle}
            aria-label={label}
            onKeyDown={e => {
                if (e.key === 'Enter' && onClick) {
                    onClick(url, externalClickHandler);
                }
            }}
        ></SiteNav.Item>
    );
};
